import TextField, { TextFieldProps } from '@mui/material/TextField';
import { IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import {
  forwardRef,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type BaseFormatType =
  | 'thousands'
  | 'currency'
  | 'percent'
  | ((v: string | number) => string);

export type BaseFormattedTextFieldProps = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'InputProps' | 'FormHelperTextProps'
> & {
  readonly value?: string | number;
  readonly onValueChange?: (v: string | number) => void;
  readonly onChange?: TextFieldProps['onChange'];

  readonly maxLength?: number;
  readonly onClear?: () => void;
  readonly extraInputAdornment?: ReactNode;
  readonly hideErrorText?: boolean;
  readonly hideClearButton?: boolean;
  readonly notFocusedHideClear?: boolean;

  readonly format?: BaseFormatType;
  readonly formatOn?: 'change' | 'blur' | 'none';
  readonly formatLocale?: string;
  readonly formatOptions?: Intl.NumberFormatOptions;

  readonly disableNumberSpinners?: boolean;
};

export const ErrorHelperTextStyle = {
  width: '100%',
  justifyContent: 'space-between',
  fontSize: '12px',
  fontStyle: 'normal' as const,
  fontWeight: 400,
  lineHeight: '18px',
  padding: 0,
  margin: '8px 50% 0 0',
  color: 'var(--dk-danger, #F55060)',
};

function buildIntlFormatter(
  locale: string,
  preset: BaseFormattedTextFieldProps['format'],
  formatOptions?: Intl.NumberFormatOptions,
) {
  const presetOptions: Intl.NumberFormatOptions | undefined =
    typeof preset === 'string'
      ? preset === 'thousands'
        ? { useGrouping: true, maximumFractionDigits: 20 }
        : preset === 'currency'
        ? { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }
        : preset === 'percent'
        ? { style: 'percent', maximumFractionDigits: 2 }
        : undefined
      : undefined;

  const options = { ...presetOptions, ...formatOptions };
  const nf = new Intl.NumberFormat(locale || 'ko-KR', options);
  return (n: number) => nf.format(n);
}

function toNumericString(input: string) {
  // 숫자, 소수점, 부호(-)만 허용
  let s = input.replace(/[^\d.-]/g, ''); // ← 하이픈을 클래스 끝에 둬서 no-useless-escape 해결
  s = s.replace(/(?!^)-/g, ''); // 맨 앞의 -만 허용
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return s;
}

const BaseFormattedTextField = forwardRef<
  HTMLDivElement,
  BaseFormattedTextFieldProps
>((props, ref) => {
  const {
    helperText,
    type,
    maxLength,
    sx,
    onClear,
    disabled,
    extraInputAdornment,
    hideErrorText = false,
    hideClearButton = false,
    notFocusedHideClear = false,

    format,
    formatOn = 'blur',
    formatLocale = 'ko-KR',
    formatOptions,
    disableNumberSpinners = false,

    value,
    onValueChange,
    onChange,

    slotProps,
    inputRef: inputRefProp,
    ...rest
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const intlFormatter = useMemo(() => {
    if (!format || typeof format === 'function') return undefined;
    return buildIntlFormatter(formatLocale, format, formatOptions);
  }, [format, formatLocale, formatOptions]);

  const [display, setDisplay] = useState<string>('');
  const [editing, setEditing] = useState(false); // 편집 중 여부
  const hasValue = value !== undefined && value !== null && `${value}` !== '';

  const useFormattedMode = Boolean(format && formatOn !== 'none');
  const effectiveType: TextFieldProps['type'] = useFormattedMode
    ? editing
      ? 'number'
      : 'text'
    : type;

  // 외부 value → display 동기화
  useEffect(() => {
    if (value === undefined || value === null || value === '') {
      setDisplay('');
      return;
    }
    if (useFormattedMode && !editing) {
      // blur 상태(표시 모드): 포맷 적용
      if (typeof format === 'function') {
        setDisplay(format(value));
      } else if (intlFormatter) {
        const n = Number(toNumericString(String(value)));
        setDisplay(isNaN(n) ? String(value) : intlFormatter(n));
      } else {
        setDisplay(String(value));
      }
    } else {
      // 편집 중에는 raw 값 그대로
      setDisplay(String(value));
    }
  }, [value, useFormattedMode, editing, format, intlFormatter]);

  // endAdornment 병합
  const inputSlotProps =
    typeof slotProps?.input === 'function' ? undefined : slotProps?.input;
  const parentEndAdornment = inputSlotProps?.endAdornment;
  const mergedInputSlotProps = {
    ...inputSlotProps,
    endAdornment: hideClearButton ? (
      parentEndAdornment ?? null
    ) : (
      <InputAdornment position="end">
        {!disabled && hasValue && (
          <IconButton
            aria-label="clear"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => {
              onValueChange?.('');
              setDisplay('');
              onClear?.();
              // 혹시나 포커스 흔들림이 있으면 다음 프레임에 재포커스
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
            disabled={disabled}
          >
            <img
              src={'/icons/close_gray.svg'}
              width={16}
              height={16}
              alt={'close icon'}
            />
          </IconButton>
        )}
        {extraInputAdornment}
        {parentEndAdornment}
      </InputAdornment>
    ),
  };

  return (
    <Stack ref={ref}>
      <TextField
        {...rest}
        inputRef={(el) => {
          inputRef.current = el;
          if (typeof inputRefProp === 'function') inputRefProp(el);
        }}
        disabled={disabled}
        fullWidth
        type={effectiveType}
        inputMode={
          effectiveType === 'number'
            ? undefined
            : type === 'number'
            ? 'decimal'
            : rest.inputMode
        }
        value={effectiveType === 'number' && value === 0 ? '' : display}
        onFocus={(e) => {
          if (useFormattedMode) {
            setEditing(true); // number로 전환
            setDisplay(value == null ? '' : String(value)); // raw 노출
          }
          rest.onFocus?.(e);
        }}
        onChange={(e) => {
          onChange?.(e); // 외부 이벤트 먼저

          const rawInput = e.target.value;

          if (!useFormattedMode) {
            if (type === 'number') {
              const numeric = Number(rawInput);
              onValueChange?.(isNaN(numeric) ? '' : numeric);
              setDisplay(rawInput);
            } else {
              onValueChange?.(rawInput);
              setDisplay(rawInput);
            }
            return;
          }

          // useFormattedMode: 편집 중에는 raw 유지
          const s = toNumericString(rawInput);
          const n = Number(s);
          const parsed: string | number = s === '' ? '' : isNaN(n) ? s : n;

          onValueChange?.(parsed);

          if (formatOn === 'change') {
            // change에도 포맷 원하면 여기서 포맷, 아니면 raw 유지
            if (typeof format === 'function') {
              setDisplay(format(parsed));
            } else if (intlFormatter && parsed !== '') {
              const nn = Number(parsed);
              setDisplay(isNaN(nn) ? String(parsed) : intlFormatter(nn));
            } else {
              setDisplay(String(parsed ?? ''));
            }
          } else {
            setDisplay(rawInput); // 입력 그대로
          }
        }}
        onBlur={(e) => {
          if (useFormattedMode) {
            setEditing(false); // blur 시 text로 전환
            const current = e.target.value;
            if (current === '') {
              setDisplay('');
            } else {
              if (typeof format === 'function') {
                setDisplay(format(current));
              } else if (intlFormatter) {
                const n = Number(toNumericString(current));
                setDisplay(isNaN(n) ? current : intlFormatter(n));
              }
            }
          }
          rest.onBlur?.(e);
        }}
        error={!!rest.error}
        helperText={
          rest.error ? (hideErrorText ? undefined : helperText) : helperText
        }
        slotProps={{
          input: mergedInputSlotProps,
          formHelperText: {
            sx: {
              ...ErrorHelperTextStyle,
              color: rest.error
                ? 'var(--dk-danger, #F55060)'
                : 'var(--dk-text-secondary, #888D96)',
              ...(typeof slotProps?.formHelperText === 'function'
                ? {}
                : slotProps?.formHelperText?.sx),
            },
            ...(typeof slotProps?.formHelperText === 'function'
              ? {}
              : slotProps?.formHelperText),
          } as any,
        }}
        sx={{
          '& .MuiInputBase-input::placeholder': { fontSize: '14px' },
          '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': {
            paddingRight: '12px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1px solid var(--dk-text, #000)',
            },
            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--dk-text, #000)',
            },
            '&.Mui-disabled': {
              backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
            },
            '&.MuiInputBase-adornedStart .MuiInputBase-input': {
              paddingLeft: '8px',
            },
          },
          '&  .MuiInputBase-root:not(.Mui-focused) .MuiInputAdornment-root': {
            display: notFocusedHideClear ? 'none' : 'inline-flex',
          },
          // 스피너는 disableNumberSpinners=true일 때만 숨김 (편집 중 표시하려면 false로 두기)
          ...(disableNumberSpinners && effectiveType === 'number'
            ? {
                '& input[type=number]': { MozAppearance: 'textfield' },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                  { WebkitAppearance: 'none', margin: 0 },
              }
            : {}),
          ...sx,
        }}
      />

      {maxLength !== undefined && (
        <Typography
          align="right"
          sx={{
            mt: rest.error || helperText ? '-18px' : '8px',
            fontSize: '14px',
          }}
        >
          {String(display ?? '').length}{' '}
          <Typography
            component="span"
            variant="inherit"
            color="var(--dk-text-disabled, #D4D7DB)"
          >
            / {maxLength}
          </Typography>
        </Typography>
      )}
    </Stack>
  );
});

BaseFormattedTextField.displayName = 'BaseFormattedTextField';
export default BaseFormattedTextField;
